import { Dialog, DialogHeader, DialogBody, DialogFooter, Radio, Rating } from '@material-tailwind/react';
import { AlertCircle, ArrowLeft, Paperclip, Download, RefreshCw, Send, RotateCw, MessageSquare, Tag, Clock, CheckCheck } from 'lucide-react';
import { AttachmentPicker } from '../../components';
import moment from 'moment';
import React, { useState } from 'react'
import { useAddSupportTicketInteraction, useAddSupportTicketResolutionAcknowledgment, useGetSupportTicketById, useSubmitSupportTicketFeedback, useApproveSupportTicket } from '../../custom-hooks/use-support';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../services/contexts/authContext';
import { SupportTicketPriorityConfig, SupportTicketStatusConfig } from '../../utils/ui-interpreters';
import { STATIC_USER_ROLES, SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES, SUPPORT_TICKET_STATUSES } from '../../utils/enums';
import { Notify } from '../../utils/functions';

const BANNER_TONES = {
    info: 'border-[#CFE0EE] bg-[#EAF1F7] text-[#3B6EA5]',
    warn: 'border-amber-200 bg-amber-50 text-amber-700',
    success: 'border-green-200 bg-green-50 text-green-700',
    neutral: 'border-[#ECE3D5] bg-cream text-[#6B675F]',
};

// Friendly status banner for the states that don't have their own action prompt.
const STATUS_BANNER = {
    [SUPPORT_TICKET_STATUSES.OPEN]: { icon: RotateCw, tone: 'info', title: "We've received your ticket", text: "Our team will review it and get back to you shortly." },
    [SUPPORT_TICKET_STATUSES.IN_PROGRESS]: { icon: RotateCw, tone: 'info', title: 'Our team is on it', text: "Your ticket is being worked on. We'll post an update here as soon as there's progress." },
    [SUPPORT_TICKET_STATUSES.PENDING_CUSTOMER]: { icon: AlertCircle, tone: 'warn', title: 'Waiting on your response', text: 'We need a bit more information from you to proceed.' },
    [SUPPORT_TICKET_STATUSES.CANCELLED]: { icon: AlertCircle, tone: 'neutral', title: 'This ticket was cancelled', text: 'This ticket is no longer active.' },
};

const CustomerSupportDetailPage = ({ handlers }) => {

    const { ticketId } = useParams();
    const navigate = useNavigate();
    const { userDetails } = useAuth();


    const [acknowledgmentDialog, setAcknowledgmentDialog] = useState(false);
    const [acknowledgmentStatus, setAcknowledgmentStatus] = useState(SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.ACKNOWLEDGED);
    const [disputeReason, setDisputeReason] = useState('');
    const [feedbackDialog, setFeedbackDialog] = useState(false);
    const [feedbackRating, setFeedbackRating] = useState(5);
    const [feedbackText, setFeedbackText] = useState('');

    const { data, isLoading: dataLoading, error: dataError } = useGetSupportTicketById(ticketId, 15 * 1000);
    const { ticket, interactions } = data || {};
    const [newMessage, setNewMessage] = useState('');
    const [messageAttachments, setMessageAttachments] = useState([]);

    const addInteractionMutation = useAddSupportTicketInteraction();
    const addResolutionAcknowledgmentMutation = useAddSupportTicketResolutionAcknowledgment();
    const submitFeedbackMutation = useSubmitSupportTicketFeedback();
    const approveTicketMutation = useApproveSupportTicket();

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTimeAgo = (date) => {
        return moment(date).fromNow();
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() && messageAttachments.length === 0) return;
        try {
            const interaction = {
                message: newMessage,
                interactionType: 'note',
                attachments: messageAttachments
            };
            await addInteractionMutation.mutateAsync({ ticketId, data: interaction });
            setNewMessage('');
            setMessageAttachments([]);
        } catch (error) {
            console.error('Error sending message:', error);
        }
    }

    const handleApproveTicket = async () => {
        try {
            await approveTicketMutation.mutateAsync(ticketId);
        } catch (error) {
            console.error('Error approving ticket:', error);
        }
    }

    const handleAcknowledgment = async () => {
        if (acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED && !disputeReason.trim()) {
            Notify("Please provide a reason for disputing the resolution", "error");
            return;
        };
        try {
            const acknowledgment = {
                acknowledgedStatus: acknowledgmentStatus,
                disputeReason: acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED ? disputeReason : null,
                clarificationRequested: acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED ? true : false,
            };
            await addResolutionAcknowledgmentMutation.mutateAsync({ ticketId, data: acknowledgment });
            setAcknowledgmentDialog(false);
            if (acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.ACKNOWLEDGED) {
                setFeedbackDialog(true);
            }
            setAcknowledgmentStatus(SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.ACKNOWLEDGED);
            setDisputeReason('');
        } catch (error) {
            console.error('Error acknowledging resolution:', error);
        }
    }

    const handleSubmitFeedback = async () => {
        try {
            // TODO: Refine the attributes in this api call
            const feedback = {
                satisfactionRating: feedbackRating,
                feedbackText: feedbackText,
                wouldRecommend: feedbackRating > 3 ? 'yes' : 'no',
                improvementSuggestions: feedbackText,
                agentRating: feedbackRating,
                responseTimeRating: feedbackRating,
                resolutionQualityRating: feedbackRating,
            };
            await submitFeedbackMutation.mutateAsync({ ticketId, data: feedback });
            setFeedbackDialog(false);
            setFeedbackRating(5);
            setFeedbackText('');
        } catch (error) {
            console.error('Error submitting feedback:', error);
        }
    }

    if (dataError) {
        return <div className="px-6 py-10 text-center text-[#8C8175]">Error: {dataError.message}</div>;
    }

    if (dataLoading) {
        return (
            <div className="flex min-h-[40vh] flex-col items-center justify-center">
                <div className="mb-4 h-10 w-10 animate-spin rounded-full border-4 border-cream-200 border-t-brand" />
                <div className="font-display text-lg font-semibold text-ink">Loading ticket details...</div>
                <div className="mt-1 text-sm text-[#8C8175]">Please wait while we fetch your support ticket.</div>
            </div>
        );
    }

    const statusConfig = SupportTicketStatusConfig(ticket.status);
    const priorityConfig = SupportTicketPriorityConfig(ticket.priority);
    let banner = STATUS_BANNER[ticket.status];
    
    // Check if support team has replied
    const hasSupportReply = interactions?.some(interaction => {
        return interaction.user?.role !== STATIC_USER_ROLES.USER && interaction.user?.role !== STATIC_USER_ROLES.TEAM_MEMBER;
    });

    // Hide OPEN banner if support has replied
    if (ticket.status === SUPPORT_TICKET_STATUSES.OPEN && hasSupportReply) {
        banner = null;
    }

    const BannerIcon = banner?.icon;

    return (
        <div className="animate-fadeIn px-4 py-5 md:px-[34px] md:py-[30px]">
            {/* Outside the centred column on purpose — anchored to the page's left edge
                rather than floating in from the content width. */}
            <button
                onClick={() => navigate(-1)}
                className="mb-3 inline-flex items-center gap-2 rounded-[11px] border-[1.5px] border-[#E6DDCF] bg-white px-3.5 py-2 font-display text-[13px] font-semibold text-[#3B2A1E] transition-colors hover:bg-cream"
            >
                <ArrowLeft className="h-4 w-4" />
                Back to tickets
            </button>
            {/* Only pulled level with the back button at 2xl. Below that the centred
                column's side margin is narrower than the button, so it would overlap. */}
            <div className="mx-auto max-w-4xl 2xl:-mt-[52px]">
                {/* Header */}
                <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h1 className="font-display text-[26px] font-bold text-ink">{ticket.subject}</h1>
                        <p className="mt-1 text-sm text-[#8C8175]">
                            <span className="font-semibold uppercase text-[#A89C8C]">#TKT-{ticket._id.slice(-5)}</span> • Created {formatDate(ticket.createdDate)}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <span className={`inline-flex items-center rounded-full border px-3.5 py-1.5 text-sm font-medium ${statusConfig.color}`}>
                            {statusConfig.label}
                        </span>
                        <span className={`inline-flex items-center rounded-full border px-3.5 py-1.5 text-sm font-medium ${priorityConfig.color}`}>
                            {priorityConfig.label} priority
                        </span>
                    </div>
                </div>

                {/* Ticket Info */}
                <div className="mb-5 rounded-2xl border border-[#ECE3D5] bg-white p-6">
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
                        <div>
                            <h4 className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-[#8C8175]">Category</h4>
                            <div className="flex items-center gap-2 text-ink">
                                <Tag className="h-4 w-4 text-brand" />
                                <span className="capitalize">{ticket.category.replace('_', ' ')}</span>
                            </div>
                        </div>
                        {ticket.assignedAgent && (
                            <div>
                                <h4 className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-[#8C8175]">Assigned Agent</h4>
                                <div className="flex items-center gap-2.5">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-cream-input text-sm font-semibold text-brand">
                                        {ticket.assignedAgent.name.charAt(0)}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="truncate font-medium text-ink">{ticket.assignedAgent.name}</p>
                                        <p className="truncate text-xs text-[#8C8175]">{ticket.assignedAgent.email}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div>
                            <h4 className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-[#8C8175]">Last Updated</h4>
                            <div className="flex items-center gap-2 text-ink">
                                <Clock className="h-4 w-4 text-brand" />
                                {formatDate(ticket.lastUpdated)}
                            </div>
                        </div>
                    </div>

                    {ticket.resolution && (
                        <div className="mt-6 border-t border-[#F1E9DC] pt-6">
                            <h4 className="mb-3 font-display font-semibold text-ink">Resolution</h4>
                            <div className="rounded-xl border border-green-200 bg-green-50 p-4">
                                <p className="leading-relaxed text-[#33553f]">{ticket.resolution}</p>
                            </div>
                        </div>
                    )}

                    {ticket.status === SUPPORT_TICKET_STATUSES.RESOLVED && ticket.requiresCustomerAcknowledgment && (
                        <div className={`mt-6 ${ticket.resolution ? '' : 'border-t border-[#F1E9DC]'} pt-6`}>
                            <div className="flex flex-wrap gap-3">
                                <button
                                    onClick={() => setAcknowledgmentDialog(true)}
                                    className="rounded-xl bg-green-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-700"
                                >
                                    Accept Resolution
                                </button>
                                <button
                                    onClick={() => {
                                        setAcknowledgmentStatus(SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED);
                                        setAcknowledgmentDialog(true);
                                    }}
                                    className="rounded-xl border-[1.5px] border-brand px-5 py-2.5 text-sm font-semibold text-brand transition-colors hover:bg-cream-input"
                                >
                                    Open Ticket Again
                                </button>
                            </div>
                        </div>
                    )}
                    {ticket.status === SUPPORT_TICKET_STATUSES.CLOSED && !ticket.customerFeedbackSubmitted && (
                        <div className={`mt-6 ${ticket.resolution ? '' : 'border-t border-[#F1E9DC]'} pt-6`}>
                            <div className="flex justify-end">
                                <button
                                    onClick={() => setFeedbackDialog(true)}
                                    className="rounded-xl border-[1.5px] border-brand px-5 py-2.5 text-sm font-semibold text-brand transition-colors hover:bg-cream-input"
                                >
                                    Leave Feedback
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Friendly status banner */}
                {banner && (
                    <div className={`mb-5 flex items-start gap-3 rounded-2xl border p-4 ${BANNER_TONES[banner.tone]}`}>
                        <BannerIcon className="mt-0.5 h-5 w-5 shrink-0" />
                        <div>
                            <h6 className="font-display font-semibold">{banner.title}</h6>
                            <p className="mt-0.5 text-sm opacity-90">{banner.text}</p>
                        </div>
                    </div>
                )}

                {/* Action Required — team member request approval */}
                {ticket.status === SUPPORT_TICKET_STATUSES.AWAITING_APPROVAL && userDetails?.role === STATIC_USER_ROLES.USER && (
                    <div className="mb-5 flex flex-col gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-800 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                            <div>
                                <h6 className="font-display font-semibold">Action Required: Team Member Request</h6>
                                <p className="mt-0.5 text-sm">This ticket was created by a team member and is awaiting your approval before being sent to support.</p>
                            </div>
                        </div>
                        <button
                            onClick={handleApproveTicket}
                            disabled={approveTicketMutation.isLoading}
                            className="flex shrink-0 items-center justify-center gap-2 rounded-xl bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-60"
                        >
                            {approveTicketMutation.isLoading && <RefreshCw className="h-4 w-4 animate-spin" />}
                            Approve Ticket
                        </button>
                    </div>
                )}

                {/* Action Required — acknowledge resolution */}
                {ticket.requiresCustomerAcknowledgment && ticket.status === SUPPORT_TICKET_STATUSES.RESOLVED && (
                    <div className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-800">
                        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                        <div>
                            <h6 className="font-display font-semibold">Action Required</h6>
                            <p className="mt-0.5 text-sm">Please review the resolution above and let us know if it resolves your issue.</p>
                        </div>
                    </div>
                )}

                {/* Attachments added when the ticket was raised */}
                {(ticket.attachments || []).length > 0 && (
                    <div className="mb-5 rounded-2xl border border-[#ECE3D5] bg-white p-6">
                        <div className="mb-4 flex items-center gap-2">
                            <Paperclip className="h-5 w-5 text-brand" />
                            <h3 className="font-display text-lg font-semibold text-ink">Attachments</h3>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {ticket.attachments.map((attachment, index) => (
                                <a
                                    key={index}
                                    href={attachment.path}
                                    download={attachment.originalName}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`Download ${attachment.originalName || 'attachment'}`}
                                    className="group inline-flex max-w-full items-center gap-2 rounded-xl bg-cream-input px-3.5 py-2.5 text-sm text-[#3B2A1E] transition-colors hover:bg-cream"
                                >
                                    <Paperclip className="h-4 w-4 shrink-0 text-[#A89C8C] group-hover:text-brand" />
                                    <span className="truncate">{attachment.originalName || `Attachment ${index + 1}`}</span>
                                    <Download className="h-4 w-4 shrink-0 text-[#A89C8C] group-hover:text-brand" />
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {/* Conversation */}
                <div className="rounded-2xl border border-[#ECE3D5] bg-white p-6">
                    <div className="mb-6 flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-brand" />
                        <h3 className="font-display text-lg font-semibold text-ink">Interactions</h3>
                    </div>
                    <div className="mb-6 space-y-5">
                        {interactions.map((interaction) => {
                            const isCustomer = interaction.user?.role === STATIC_USER_ROLES.USER;
                            const isTeamMember = interaction.user?.role === STATIC_USER_ROLES.TEAM_MEMBER;
                            const isCurrentUser = interaction.user?._id === userDetails?._id;
                            const userName = isCurrentUser ? (interaction.user?.name || 'You') : (interaction.user?.name || 'System');
                            const isOwnerOrTeam = isCustomer || isTeamMember;
                            const isFeedback = interaction.isFeedback;

                            if (isFeedback) {
                                return (
                                    <div key={interaction._id} className="my-4 flex justify-center">
                                        <div className="max-w-[90%] rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4 text-center shadow-sm md:max-w-[70%]">
                                            <div className="mb-2 flex justify-center">
                                                <Rating value={interaction.rating || 5} readonly />
                                            </div>
                                            <div className="mb-2 text-xs font-medium text-amber-700">
                                                {interaction?.user?.name || 'Customer'} submitted feedback · {getTimeAgo(interaction?.createdDate)}
                                            </div>
                                            <p className="text-sm italic text-[#6B675F]">
                                                "{interaction?.message?.split('. Comment: ')[1] || 'No additional comments provided.'}"
                                            </p>
                                        </div>
                                    </div>
                                );
                            }

                            const bubbleClass = isCustomer
                                ? 'bg-brand text-white'
                                : isTeamMember
                                    ? 'bg-[#A9611F] text-white'
                                    : 'border border-[#ECE3D5] bg-[#F4EDE1] text-ink';

                            return (
                                <div key={interaction._id} className={`flex ${isOwnerOrTeam ? 'justify-end' : 'justify-start'}`}>
                                    <div className="max-w-[85%]">
                                        <div className={`rounded-2xl px-4 py-3 ${bubbleClass}`}>
                                            <div className="mb-1.5 flex items-center gap-2">
                                                <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${isOwnerOrTeam ? 'bg-white/25 text-white' : 'bg-cream-input text-brand'}`}>
                                                    {userName.charAt(0)}
                                                </div>
                                                <span className={`text-sm font-medium ${isOwnerOrTeam ? 'text-white' : 'text-ink'}`}>{userName}</span>
                                                {(!isCustomer || isCurrentUser) && (
                                                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${isOwnerOrTeam ? 'bg-white/20 text-white' : 'bg-cream-input text-[#8C8175]'}`}>
                                                        {isCurrentUser ? 'You' : isTeamMember ? 'Team' : 'Agent'}
                                                    </span>
                                                )}
                                            </div>
                                            {interaction.message && (
                                                <p className="whitespace-pre-wrap text-sm leading-relaxed">{interaction.message}</p>
                                            )}
                                            {interaction.attachments.length > 0 && (
                                                <div className={`space-y-1 ${interaction.message ? `mt-3 border-t pt-3 ${isOwnerOrTeam ? 'border-white/20' : 'border-[#ECE3D5]'}` : ''}`}>
                                                    {interaction.attachments.map((attachment, index) => (
                                                        <a
                                                            key={index}
                                                            href={attachment.path}
                                                            download={attachment.originalName}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={`Download ${attachment.originalName}`}
                                                            className="flex items-center gap-2 hover:underline"
                                                        >
                                                            <Paperclip className="h-4 w-4 shrink-0" />
                                                            <span className="truncate text-sm">{attachment.originalName}</span>
                                                            <Download className="h-4 w-4 shrink-0 opacity-70" />
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`mt-1 flex justify-between gap-2 px-1 text-xs text-[#A89C8C] ${isOwnerOrTeam ? 'flex-row-reverse' : ''}`}>
                                            <div className="flex items-center gap-1">
                                                <span>{formatDate(interaction.createdDate)}</span>
                                                {isOwnerOrTeam && interaction.seenBy?.some(u => {
                                                    if (u.role) return u.role !== STATIC_USER_ROLES.USER && u.role !== STATIC_USER_ROLES.TEAM_MEMBER;
                                                    return (u._id || u) !== ticket.customer._id && (u._id || u) !== interaction.user._id;
                                                }) && (
                                                    <span className="text-teal-600" title="Seen by support team">
                                                        <CheckCheck className="h-3.5 w-3.5" />
                                                    </span>
                                                )}
                                            </div>
                                            <span>{getTimeAgo(interaction.createdDate)}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Reply Box */}
                    {ticket.status !== SUPPORT_TICKET_STATUSES.CLOSED && ticket.status !== SUPPORT_TICKET_STATUSES.CANCELLED && (
                        <div className="border-t border-[#F1E9DC] pt-6">
                            <h4 className="mb-3 font-display font-semibold text-ink">Add a reply</h4>
                            <div className="flex items-start gap-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand text-sm font-semibold text-white">
                                    {(userDetails?.name || 'U').charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <textarea
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        rows={3}
                                        placeholder="Write a message to support..."
                                        className="w-full resize-none rounded-xl border-[1.5px] border-cream-200 bg-white p-3.5 text-sm text-[#2B2420] outline-none transition-colors placeholder:text-[#B7AC9E] focus:border-brand"
                                    />
                                    <div className="mt-2 flex items-start justify-between gap-3">
                                        <AttachmentPicker
                                            attachments={messageAttachments}
                                            onChange={setMessageAttachments}
                                            disabled={addInteractionMutation.isLoading}
                                            label="Attach"
                                        />
                                        <button
                                            onClick={handleSendMessage}
                                            disabled={(!newMessage.trim() && messageAttachments.length === 0) || addInteractionMutation.isLoading}
                                            className="flex shrink-0 items-center gap-2 rounded-xl bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {addInteractionMutation.isLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                            Send
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Acknowledgment Dialog */}
            <Dialog open={acknowledgmentDialog} handler={() => setAcknowledgmentDialog(false)} className="rounded-2xl">
                <DialogHeader className="border-b border-[#ECE3D5] font-display text-ink">
                    Resolution Response
                </DialogHeader>
                <DialogBody>
                    <div className="space-y-4">
                        <p className="text-[#6B675F]">How would you like to respond to the resolution?</p>
                        <div className="flex flex-col gap-8">
                            <Radio
                                name="description"
                                value={SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.ACKNOWLEDGED}
                                checked={acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.ACKNOWLEDGED}
                                onChange={(e) => setAcknowledgmentStatus(e.target.value)}
                                label={
                                    <div>
                                        <p className="font-medium">Accept Resolution</p>
                                        <p className="font-normal">The issue has been resolved to my satisfaction</p>
                                    </div>
                                }
                                containerProps={{ className: "-mt-5" }}
                            />
                            <Radio
                                name="description"
                                value={SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED}
                                checked={acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED}
                                onChange={(e) => setAcknowledgmentStatus(e.target.value)}
                                label={
                                    <div>
                                        <p className="font-medium">Open Ticket Again</p>
                                        <p className="font-normal">The issue is not resolved or needs clarification</p>
                                    </div>
                                }
                                containerProps={{ className: "-mt-5" }}
                            />
                        </div>

                        {acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED && (
                            <div>
                                <label className="mb-1.5 block text-[12.5px] font-semibold text-[#3B2A1E]">Please explain why you're disputing this resolution</label>
                                <textarea
                                    required
                                    value={disputeReason}
                                    onChange={(e) => setDisputeReason(e.target.value)}
                                    rows={4}
                                    className="w-full resize-none rounded-xl border-[1.5px] border-cream-200 bg-white p-3.5 text-sm text-[#2B2420] outline-none transition-colors focus:border-brand"
                                />
                            </div>
                        )}
                    </div>
                </DialogBody>
                <DialogFooter className="border-t border-[#ECE3D5]">
                    <button
                        onClick={() => {
                            setAcknowledgmentDialog(false);
                            setAcknowledgmentStatus(SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.ACKNOWLEDGED);
                            setDisputeReason('');
                        }}
                        disabled={addResolutionAcknowledgmentMutation.isLoading}
                        className="mr-3 rounded-xl px-5 py-2.5 text-sm font-semibold text-[#6B675F] transition-colors hover:bg-cream disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleAcknowledgment}
                        disabled={(acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED && !disputeReason.trim()) || addResolutionAcknowledgmentMutation.isLoading}
                        className="flex items-center gap-2 rounded-xl bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {addResolutionAcknowledgmentMutation.isLoading && <RefreshCw className="h-4 w-4 animate-spin" />}
                        Submit Response
                    </button>
                </DialogFooter>
            </Dialog>

            {/* Feedback Dialog */}
            <Dialog open={feedbackDialog} handler={() => setFeedbackDialog(false)} className="rounded-2xl">
                <DialogHeader className="border-b border-[#ECE3D5]">
                    <div className="flex flex-col">
                        <h4 className="font-display font-semibold text-ink">Leave Feedback</h4>
                        <p className="text-sm text-[#8C8175]">We value your feedback and will use it to improve our support.</p>
                    </div>
                </DialogHeader>
                <DialogBody>
                    <div className="space-y-6">
                        <div>
                            <h4 className="mb-4 font-display font-semibold text-ink">How satisfied are you with the support?</h4>
                            <div className="flex items-center gap-2">
                                <Rating unratedColor="amber" ratedColor="amber" value={feedbackRating} onChange={(value) => setFeedbackRating(value)} />
                                <span className="ml-3 text-[#6B675F]">
                                    {feedbackRating === 1 && 'Very Poor'}
                                    {feedbackRating === 2 && 'Poor'}
                                    {feedbackRating === 3 && 'Average'}
                                    {feedbackRating === 4 && 'Good'}
                                    {feedbackRating === 5 && 'Excellent'}
                                </span>
                            </div>
                        </div>
                        <div>
                            <label className="mb-1.5 block text-[12.5px] font-semibold text-[#3B2A1E]">Additional Comments (optional)</label>
                            <textarea
                                value={feedbackText}
                                onChange={(e) => setFeedbackText(e.target.value)}
                                rows={4}
                                placeholder="Tell us about your experience..."
                                className="w-full resize-none rounded-xl border-[1.5px] border-cream-200 bg-white p-3.5 text-sm text-[#2B2420] outline-none transition-colors placeholder:text-[#B7AC9E] focus:border-brand"
                            />
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter className="border-t border-[#ECE3D5]">
                    <button
                        onClick={() => {
                            setFeedbackDialog(false);
                            setFeedbackRating(5);
                            setFeedbackText('');
                        }}
                        disabled={submitFeedbackMutation.isLoading}
                        className="mr-3 rounded-xl px-5 py-2.5 text-sm font-semibold text-[#6B675F] transition-colors hover:bg-cream disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmitFeedback}
                        disabled={submitFeedbackMutation.isLoading}
                        className="flex items-center gap-2 rounded-xl bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-60"
                    >
                        {submitFeedbackMutation.isLoading && <RefreshCw className="h-4 w-4 animate-spin" />}
                        Submit Feedback
                    </button>
                </DialogFooter>
            </Dialog>
        </div>
    );
}

export default CustomerSupportDetailPage
